import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { type AmazonAuthorizationRow, marketplaceDisplayName } from "@/lib/amazon-authorization";

export interface ShopDeletionTarget {
  /** One marketplace site, or the whole store: every site of one seller. */
  kind: "site" | "store";
  name: string;
  rows: readonly AmazonAuthorizationRow[];
}

export function DeleteShopsDialog({
  target,
  pending,
  onCancel,
  onConfirm,
}: {
  target: ShopDeletionTarget | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (target: ShopDeletionTarget) => void;
}): React.ReactElement {
  const { i18n, t } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const language = i18n.resolvedLanguage ?? i18n.language;
  const sites = new Intl.ListFormat(language, { type: "conjunction" }).format(
    target?.rows.map((row) => marketplaceDisplayName(row.shop.marketplaceName, language)) ?? [],
  );
  const kind = target?.kind ?? "site";

  return (
    <AlertDialog
      open={target !== null}
      onOpenChange={(open) => {
        // Deletion runs in one server transaction; closing mid-way would hide
        // whether it finished, so the dialog stays until the request settles.
        if (!open && !pending) onCancel();
      }}
    >
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(`amazonAuthorization.delete.${kind}Title`, { name: target?.name ?? "" })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("amazonAuthorization.delete.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="mx-6 mb-6 rounded-[10px] border border-line bg-subtle px-3 py-2.5 text-[12px] leading-5 text-muted-foreground">
          <p className="font-medium text-ink">
            {t("amazonAuthorization.delete.sites", { count: target?.rows.length ?? 0, sites })}
          </p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
            <li>{t("amazonAuthorization.delete.dataScope")}</li>
            <li>{t("amazonAuthorization.delete.aiScope")}</li>
            <li>{t("amazonAuthorization.delete.authorization")}</li>
            <li>{t("amazonAuthorization.delete.amazonUntouched")}</li>
          </ul>
        </div>
        <AlertDialogFooter>
          <Button variant="outline" disabled={pending} onClick={onCancel}>
            {tCommon("actions.cancel")}
          </Button>
          <Button
            variant="destructive"
            loading={pending}
            disabled={pending}
            onClick={() => {
              if (target) onConfirm(target);
            }}
          >
            {t(`amazonAuthorization.delete.${kind}Confirm`)}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
